import { Button, Form, Input, message } from "antd";
import { LockOutlined, WhatsAppOutlined } from "@ant-design/icons";
import "./style.scss";
import { useForm } from "antd/es/form/Form";
import { useEffect, useState } from "react";
import Api from "../../Network";
import { setActiveChat, setInstanceContacts } from "../../redux/action";
import { useDispatch } from "react-redux";
import loginIconUrl from "../../icons/loginIcon.svg?url";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import awfarLogo from "../../assets/logo.png";
import { useTheme } from "../../Hooks/useTheme";

type FormValues = {
  email: string;
  password: string;
};

export const Login = () => {
  const { isDark } = useTheme();
  const [form] = useForm();
  const [loading, setLoading] = useState<boolean>();
  // Account that was refused because its company is disabled; swaps the form for a notice
  const [disabledAccount, setDisabledAccount] = useState<string | null>(null);
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  // Clear any leftover logout flags once we reach the login screen. Keeping these flags until
  // this point is what keeps pending interceptor / getToken callbacks silent during navigation.
  useEffect(() => {
    localStorage.removeItem("isLoggingOut");
    localStorage.removeItem("silentLogout");
  }, []);

  // The form is unmounted while the notice shows, so clear the password once it is back
  useEffect(() => {
    if (!disabledAccount) form.resetFields(["password"]);
  }, [disabledAccount, form]);

  const handleOnLogin = async (values: FormValues) => {
    setLoading(true);

    try {
      const response = await Api.login({
        email: values.email,
        password: values.password,
      });
      
      // Clear chat state
      dispatch(setActiveChat(null));
      dispatch(setInstanceContacts(null));

      setLoading(false);
      // Token is in localStorage — App.tsx will load user via auth/me + company.
      // Do not call auth/me here (duplicates App + PrivateRoute and requestManager cancels one).
      // Do not full-page reload — it aborts in-flight XHR (auth/me, segments, etc.).
      history.push("/");
    } catch (error: any) {
      const status = error?.response?.status;
      const responseData = error?.response?.data;

      if (responseData?.message === "company_disabled") {
        setDisabledAccount(values.email);
        setLoading(false);
        return;
      }

      const isProduction = import.meta.env.MODE === 'production' || import.meta.env.VITE_ENV === 'production';
      
      // Check for validation errors first (has errors object or status: "error")
      const hasValidationErrors = responseData?.errors || responseData?.status === "error";
      
      let errorMessage = '';
      
      // Extract validation error messages
      if (hasValidationErrors) {
        if (responseData?.errors) {
          // Handle errors object (e.g., { error: "Unauthorized" })
          if (typeof responseData.errors === 'object' && !Array.isArray(responseData.errors)) {
            const errorMessages = Object.values(responseData.errors).filter(msg => msg);
            errorMessage = errorMessages.join(', ');
          } else if (Array.isArray(responseData.errors)) {
            errorMessage = responseData.errors.join(', ');
          } else if (typeof responseData.errors === 'string') {
            errorMessage = responseData.errors;
          }
        }
        
        // Fallback to message if errors object is empty
        if (!errorMessage && responseData?.message) {
          errorMessage = responseData.message;
        }
      } else {
        // Use generic error message if no validation errors
        errorMessage = responseData?.message || error?.message;
      }

      // If the error is "Unauthorized", show a more user-friendly message
      if (errorMessage === "Unauthorized") {
        errorMessage = t("general.Wrong_User_Or_Password");
      }
      
      // Handle 500 and 403 errors - hide completely in production
      if (status === 500 || status === 403) {
        if (!isProduction) {
          // Show detailed error in development/staging with status code
          const defaultMessage = status === 500 ? 'Internal Server Error' : 'Forbidden';
          message.error(`${errorMessage || defaultMessage} (${status})`);
        }
        // In production, silently fail (no error message shown)
      } 
      // Handle 401 (Unauthorized) and validation errors - always show
      else if (status === 401 || hasValidationErrors) {
        const finalMessage = errorMessage || t("general.Wrong_User_Or_Password");
        // In development/staging, include status code
        if (!isProduction && status) {
          message.error(`${finalMessage} (${status})`);
        } else {
          message.error(finalMessage);
        }
      }
      // Handle validation errors (400, 422, etc.) - always show
      else if (status === 400 || status === 422) {
        const finalMessage = errorMessage || t("general.Wrong_User_Or_Password");
        // In development/staging, include status code
        if (!isProduction && status) {
          message.error(`${finalMessage} (${status})`);
        } else {
          message.error(finalMessage);
        }
      }
      // Handle other errors
      else {
        const finalMessage = errorMessage || t("general.Wrong_User_Or_Password");
        // In development/staging, include status code
        if (!isProduction && status) {
          message.error(`${finalMessage} (${status})`);
        } else {
          message.error(finalMessage);
        }
      }
      
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper">
      <div className="login-form">
        {/* The navy logo disappears on the dark card; dark uses the white one */}
        <img src={isDark ? "/assets/awfarLogo.webp" : awfarLogo} width={200} alt="logo" />
        {disabledAccount ? (
          <div className="login-company-disabled">
            <div className="login-company-disabled__icon">
              <LockOutlined />
            </div>
            <h3>{t("general.Company_Disabled_Title")}</h3>
            <p>{t("general.Company_Disabled")}</p>
            <span className="login-company-disabled__account">{disabledAccount}</span>
            <Button
              type="primary"
              onClick={() => setDisabledAccount(null)}
            >
              {t("general.Company_Disabled_Back")}
            </Button>
            <Button
              className="login-company-disabled__support"
              icon={<WhatsAppOutlined />}
              href="https://wa.me/+20225865866"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t("general.Contact_Support")}
            </Button>
          </div>
        ) : (
          <>
            <p>{t("general.login")}</p>
            <Form
              layout="vertical"
              className="login-form-wrapper"
              name="login-form"
              form={form}
              onFinish={handleOnLogin}
            >
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: `${t("general.Email_is_required")}`,
                  },
                ]}
              >
                <Input placeholder={t("general.Email")} />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: `${t("general.Password_is_required")}`,
                  },
                ]}
              >
                <Input.Password placeholder={t("general.Password")} />
              </Form.Item>

              <Button loading={loading} type="primary" htmlType="submit">
                {t("general.Sign_In")}
              </Button>
            </Form>
          </>
        )}
      </div>
      <div className="login-icon-container">
        <img src={loginIconUrl} alt="" />
      </div>
    </div>
  );
};
